/**
 * Shared "odometer" digit-roll counter animation and subscriber-count poller
 * used by:
 *  - sections/acerca-de-banner.liquid (single-channel YouTube subscriber counter)
 *  - sections/acerca-youtube-counter.liquid (multi-channel YouTube subscriber counter)
 *
 * Exposes a small dependency-free global namespace, window.AcercaCounterAnimation:
 *   - Pure, stateless helpers: isSeparator, countDigits, hashString, seededUnit,
 *     digitMarkup, buildOdometer, revealStartFormatted, formatNumber.
 *   - createOdometerCounter(config): a factory returning an isolated instance
 *     (own timers/animation state) exposing renderNumber/animateTo/scheduleFluctuation/
 *     playRevealAnimation/isRevealAnimating/stop/reset, so multiple sections (and,
 *     for the multi-channel section, channel switches within one section) never
 *     share mutable animation state.
 *   - createSubscriberPoller(config): a factory returning an isolated instance
 *     that owns fetching, exponential backoff, AbortController cancellation, and
 *     sessionStorage caching against the Cloudflare Worker proxy — the same
 *     mechanics for both sections instead of two independently-drifting copies.
 *
 * Each section's own {% javascript %} block still owns: DOM lookups, which
 * channel id(s) to poll, IntersectionObserver/MutationObserver wiring, and the
 * per-section visual-offset ("fluctuation") formula — those are intentionally
 * NOT unified here since they differ in the underlying business logic between
 * the two sections.
 */
(function () {
  'use strict';

  if (window.AcercaCounterAnimation) return;

  var DEFAULT_DIGIT_DURATION = 2800;
  var DEFAULT_REVEAL_DIGIT_DURATION = 5200;
  var DEFAULT_SHARED_OFFSET_INTERVAL = 12000;

  /* ── Pure helpers ── */

  function isSeparator(ch) {
    return ch === ',' || ch === '.';
  }

  function countDigits(formatted) {
    var total = 0;
    for (var i = 0; i < formatted.length; i++) {
      if (!isSeparator(formatted.charAt(i))) total += 1;
    }
    return total;
  }

  function hashString(value) {
    var hash = 2166136261;
    for (var i = 0; i < value.length; i++) {
      hash ^= value.charCodeAt(i);
      hash += (hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24);
    }
    return hash >>> 0;
  }

  function seededUnit(seed) {
    return hashString(String(seed)) / 4294967295;
  }

  function digitMarkup(ch, classNames) {
    if (isSeparator(ch)) {
      return '<span class="' + classNames.separator + '" aria-hidden="true">' + ch + '</span>';
    }
    return [
      '<span class="' + classNames.digit + '" data-value="' + ch + '">',
        '<span class="' + classNames.reel + '" aria-hidden="true" style="transition-duration: 0ms; transform: translate3d(0, -' + Number(ch) + 'em, 0);">',
          '<span>0</span><span>1</span><span>2</span><span>3</span><span>4</span>',
          '<span>5</span><span>6</span><span>7</span><span>8</span><span>9</span>',
        '</span>',
      '</span>'
    ].join('');
  }

  function buildOdometer(formatted, classNames) {
    return formatted.split('').map(function (ch) {
      return digitMarkup(ch, classNames);
    }).join('');
  }

  function revealStartFormatted(targetFormatted, unitForDigit) {
    var output = '';
    var digitIndex = 0;

    for (var i = 0; i < targetFormatted.length; i++) {
      var ch = targetFormatted.charAt(i);
      if (isSeparator(ch)) {
        output += ch;
        continue;
      }

      var targetDigit = Number(ch);
      var startDigit = (targetDigit + 1 + Math.floor(unitForDigit(digitIndex) * 9)) % 10;
      if (digitIndex === 0 && startDigit === 0) {
        startDigit = targetDigit === 1 ? 2 : 1;
      }
      output += String(startDigit);
      digitIndex += 1;
    }

    return output;
  }

  function formatNumber(value, minimumIntegerDigits) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'es-MX', {
        minimumIntegerDigits: minimumIntegerDigits || 1
      }).format(Math.max(0, Math.round(value)));
    } catch (error) {
      var raw = Math.max(0, Math.round(value)).toString();
      if (minimumIntegerDigits) raw = raw.padStart(minimumIntegerDigits, '0');
      return raw.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }
  }

  /**
   * Creates an isolated odometer counter instance bound to its own countEl and
   * config. No state is shared between instances (or between calls for
   * different active channels on the same instance) other than what the
   * caller closes over in its config callbacks.
   *
   * config:
   *   countEl              {Element}  required, element whose content is rendered
   *   skeletonEl            {Element}  optional, loading skeleton to remove on render
   *   classNames             {Object}  required, { digit, reel, separator } CSS class names
   *   formatAriaLabel        {Function} required, (formattedValue) -> aria-label string
   *   useVisualOffset        {Boolean} required, whether fluctuation is enabled
   *   canAnimate             {Function} optional, () -> boolean, gates scheduleFluctuation
   *   hasConfirmed           {Function} required, () -> boolean, gates reveal/fluctuation
   *   getTarget              {Function} required, () -> number, current confirmed+offset value
   *   updateOffset           {Function} required, (now) -> void, recomputes + stores the
   *                          instance's own visual offset (side effect on caller's state)
   *   seedFn                 {Function} required, (suffix) -> unit number in [0,1),
   *                          called at invocation time so per-channel seeding stays live
   *                          across channel switches
   *   defaultDigitDuration    {Number}  optional, default 2800
   *   revealDigitDuration     {Number}  optional, default 5200
   *   sharedOffsetInterval    {Number}  optional, default 12000
   */
  function createOdometerCounter(config) {
    config = config || {};
    var countEl = config.countEl;
    var classNames = config.classNames || {};
    var defaultDigitDuration = config.defaultDigitDuration || DEFAULT_DIGIT_DURATION;
    var revealDigitDuration = config.revealDigitDuration || DEFAULT_REVEAL_DIGIT_DURATION;
    var sharedOffsetInterval = config.sharedOffsetInterval || DEFAULT_SHARED_OFFSET_INTERVAL;

    var state = {
      animationToken: 0,
      animationTimer: null,
      revealFrame: null,
      fluctuationTimer: null,
      isRevealAnimating: false,
      lastFormatted: ''
    };

    function renderNumber(value, options) {
      options = options || {};
      if (config.skeletonEl) config.skeletonEl.remove();

      var duration = Number(options.duration);
      if (!Number.isFinite(duration)) duration = defaultDigitDuration;
      var stagger = options.stagger === true;
      var staggerStep = Number(options.staggerStep) || 0;

      var formatted = formatNumber(value, options.minimumIntegerDigits);
      countEl.setAttribute('aria-label', config.formatAriaLabel(formatted));

      var current = countEl.getAttribute('data-formatted-value') || state.lastFormatted || '';
      if (!current || current.length !== formatted.length) {
        countEl.innerHTML = buildOdometer(formatted, classNames);
        countEl.setAttribute('data-formatted-value', formatted);
        state.lastFormatted = formatted;
        return;
      }
      if (formatted === current) return;

      var digitEls = countEl.querySelectorAll('.' + classNames.digit);
      var digitIndex = 0;
      for (var i = 0; i < formatted.length; i++) {
        var oldCh = current.charAt(i) || '';
        var newCh = formatted.charAt(i) || '';

        if (isSeparator(oldCh) !== isSeparator(newCh)) {
          countEl.innerHTML = buildOdometer(formatted, classNames);
          countEl.setAttribute('data-formatted-value', formatted);
          state.lastFormatted = formatted;
          return;
        }
        if (isSeparator(oldCh) || isSeparator(newCh)) continue;

        var digit = digitEls[digitIndex];
        var currentDigitIndex = digitIndex;
        digitIndex += 1;
        if (!digit || oldCh === newCh) continue;

        digit.setAttribute('data-value', newCh);
        var reel = digit.querySelector('.' + classNames.reel);
        if (!reel) continue;
        reel.style.transitionDelay = stagger ? (currentDigitIndex * staggerStep) + 'ms' : '0ms';
        reel.style.transitionDuration = duration + 'ms';
        reel.style.transform = 'translate3d(0, -' + Number(newCh) + 'em, 0)';
      }

      countEl.setAttribute('data-formatted-value', formatted);
      state.lastFormatted = formatted;
    }

    function animateTo(target, duration, onComplete, animationOptions) {
      state.animationToken += 1;
      var currentAnimation = state.animationToken;
      if (state.animationTimer) clearTimeout(state.animationTimer);
      if (state.revealFrame) cancelAnimationFrame(state.revealFrame);

      animationOptions = animationOptions || {};
      var renderOptions = {
        minimumIntegerDigits: animationOptions.minimumIntegerDigits,
        duration: duration || defaultDigitDuration,
        stagger: animationOptions.stagger === true,
        staggerStep: Number(animationOptions.staggerStep) || 0
      };
      var completionDelay = Number(animationOptions.completionDelay) || 0;

      renderNumber(Math.round(target), renderOptions);
      if (onComplete) {
        state.animationTimer = setTimeout(function () {
          if (currentAnimation === state.animationToken) onComplete();
        }, (duration || defaultDigitDuration) + completionDelay);
      }
    }

    function scheduleFluctuation() {
      if (state.fluctuationTimer) clearTimeout(state.fluctuationTimer);
      if (!config.useVisualOffset) return;
      if (config.canAnimate && !config.canAnimate()) return;
      if (config.hasConfirmed && !config.hasConfirmed()) return;

      state.fluctuationTimer = setTimeout(function () {
        config.updateOffset(Date.now());
        animateTo(config.getTarget(), defaultDigitDuration);
        scheduleFluctuation();
      }, sharedOffsetInterval - (Date.now() % sharedOffsetInterval) + 60);
    }

    function playRevealAnimation() {
      if (!config.hasConfirmed()) return;

      var target = config.getTarget();
      var targetFormatted = formatNumber(target);
      var revealStaggerStep = 90;
      var seedPrefix = Math.floor(Date.now() / sharedOffsetInterval) + ':reveal';
      var startFormatted = revealStartFormatted(targetFormatted, function (digitIndex) {
        return config.seedFn(seedPrefix + ':' + digitIndex);
      });

      if (state.fluctuationTimer) clearTimeout(state.fluctuationTimer);
      if (state.animationTimer) clearTimeout(state.animationTimer);
      if (state.revealFrame) cancelAnimationFrame(state.revealFrame);

      state.isRevealAnimating = true;
      state.lastFormatted = '';
      countEl.removeAttribute('data-formatted-value');
      countEl.innerHTML = buildOdometer(startFormatted, classNames);
      countEl.setAttribute('data-formatted-value', startFormatted);
      countEl.setAttribute('aria-label', config.formatAriaLabel(targetFormatted));

      state.revealFrame = requestAnimationFrame(function () {
        state.revealFrame = requestAnimationFrame(function () {
          animateTo(target, revealDigitDuration, function () {
            state.isRevealAnimating = false;
            renderNumber(config.getTarget(), { duration: defaultDigitDuration });
            scheduleFluctuation();
          }, {
            stagger: true,
            staggerStep: revealStaggerStep,
            completionDelay: Math.max(0, countDigits(targetFormatted) - 1) * revealStaggerStep
          });
        });
      });
    }

    function stopFluctuation() {
      if (state.fluctuationTimer) { clearTimeout(state.fluctuationTimer); state.fluctuationTimer = null; }
    }

    function stop() {
      stopFluctuation();
      if (state.animationTimer) { clearTimeout(state.animationTimer); state.animationTimer = null; }
      if (state.revealFrame) { cancelAnimationFrame(state.revealFrame); state.revealFrame = null; }
    }

    function cancelAnimation() {
      stop();
      state.isRevealAnimating = false;
    }

    function clearFormattedState() {
      state.lastFormatted = '';
      if (countEl) countEl.removeAttribute('data-formatted-value');
    }

    function reset() {
      cancelAnimation();
      clearFormattedState();
    }

    return {
      renderNumber: renderNumber,
      animateTo: animateTo,
      scheduleFluctuation: scheduleFluctuation,
      playRevealAnimation: playRevealAnimation,
      isRevealAnimating: function () { return state.isRevealAnimating; },
      stop: stop,
      stopFluctuation: stopFluctuation,
      cancelAnimation: cancelAnimation,
      clearFormattedState: clearFormattedState,
      reset: reset
    };
  }

  /* ── Shared fetch/poll/backoff/cache logic ── */

  var DEFAULT_MAX_BACKOFF_MS = 300000; /* 5 minutes */
  var DEFAULT_CACHE_TTL_MS = 60 * 60 * 1000; /* 1 hour */

  function cacheKey(channelId) {
    return 'ytc-subscriber-count-' + channelId;
  }

  function getCachedChannelCount(channelId, cacheTtlMs) {
    try {
      var cached = JSON.parse(sessionStorage.getItem(cacheKey(channelId)));
      if (cached && cached.timestamp && (Date.now() - cached.timestamp) < cacheTtlMs) {
        return cached.count;
      }
    } catch (error) { /* ignore */ }
    return null;
  }

  function setCachedChannelCount(channelId, count) {
    try {
      sessionStorage.setItem(cacheKey(channelId), JSON.stringify({ count: count, timestamp: Date.now() }));
    } catch (error) { /* ignore */ }
  }

  function fetchChannelCount(proxyUrl, channelId, signal) {
    var url = proxyUrl + '?channelId=' + encodeURIComponent(channelId) + '&_=' + Date.now();
    return fetch(url, { method: 'GET', signal: signal })
      .then(function (response) {
        if (!response.ok) throw new Error('Proxy response not OK: ' + response.status);
        return response.json();
      })
      .then(function (data) {
        return typeof data.subscriberCount === 'number' ? data.subscriberCount : 0;
      });
  }

  /**
   * Creates an isolated poller instance against the subscriber-count proxy.
   * Owns: fetch + AbortController cancellation, exponential backoff on
   * failure (reset on success), and optional per-channel sessionStorage
   * caching. Sums counts across every id returned by getChannelIds() each
   * cycle, so it fits both a fixed multi-channel total (banner) and a single
   * dynamically-switched active channel (counter section).
   *
   * config:
   *   proxyUrl        {String}   required
   *   pollIntervalMs  {Number}   required, base delay between successful cycles
   *   maxBackoffMs    {Number}   optional, default 300000 — cap for backoff after failures
   *   useCache        {Boolean}  optional, default false — cache each channel's count in sessionStorage
   *   cacheTtlMs      {Number}   optional, default 3600000
   *   getChannelIds   {Function} required, () -> string[], re-evaluated every cycle so the
   *                    caller can change which channel(s) are being polled (e.g. on switch)
   *   onSuccess       {Function} required, (total) -> void
   *   onError         {Function} optional, () -> void, called once per failed cycle
   *   onCachedTotal   {Function} optional, (total) -> void, called synchronously from
   *                    start()/restart() when every requested channel has a fresh cache entry
   *
   * Returns { start, stop, restart }.
   */
  function createSubscriberPoller(config) {
    config = config || {};
    var proxyUrl = config.proxyUrl;
    var pollIntervalMs = config.pollIntervalMs;
    var maxBackoffMs = config.maxBackoffMs || DEFAULT_MAX_BACKOFF_MS;
    var useCache = config.useCache === true;
    var cacheTtlMs = config.cacheTtlMs || DEFAULT_CACHE_TTL_MS;

    var currentIntervalMs = pollIntervalMs;
    var pollTimer = null;
    var abortController = null;

    function cachedTotal(channelIds) {
      var total = 0;
      for (var i = 0; i < channelIds.length; i++) {
        var count = getCachedChannelCount(channelIds[i], cacheTtlMs);
        if (count === null) return null;
        total += count;
      }
      return total;
    }

    function runCycle() {
      var channelIds = (config.getChannelIds() || []).filter(Boolean);
      if (channelIds.length === 0) return Promise.resolve();

      if (abortController) abortController.abort();
      abortController = new AbortController();
      var signal = abortController.signal;

      return Promise.all(channelIds.map(function (channelId) {
        return fetchChannelCount(proxyUrl, channelId, signal).then(function (count) {
          if (useCache) setCachedChannelCount(channelId, count);
          return count;
        });
      }))
        .then(function (counts) {
          currentIntervalMs = pollIntervalMs;
          var total = counts.reduce(function (sum, count) { return sum + count; }, 0);
          config.onSuccess(total);
        })
        .catch(function (error) {
          if (error && error.name === 'AbortError') return;
          currentIntervalMs = Math.min(currentIntervalMs * 2, maxBackoffMs);
          if (config.onError) config.onError();
        });
    }

    function scheduleNext() {
      pollTimer = setTimeout(function () {
        runCycle().then(scheduleNext);
      }, currentIntervalMs);
    }

    function stop() {
      if (pollTimer) { clearTimeout(pollTimer); pollTimer = null; }
      if (abortController) { abortController.abort(); abortController = null; }
    }

    function start() {
      stop();
      currentIntervalMs = pollIntervalMs;

      if (useCache && config.onCachedTotal) {
        var channelIds = (config.getChannelIds() || []).filter(Boolean);
        if (channelIds.length > 0) {
          var total = cachedTotal(channelIds);
          if (total !== null) config.onCachedTotal(total);
        }
      }

      runCycle().then(scheduleNext);
    }

    function restart() {
      start();
    }

    return { start: start, stop: stop, restart: restart };
  }

  window.AcercaCounterAnimation = {
    isSeparator: isSeparator,
    countDigits: countDigits,
    hashString: hashString,
    seededUnit: seededUnit,
    digitMarkup: digitMarkup,
    buildOdometer: buildOdometer,
    revealStartFormatted: revealStartFormatted,
    formatNumber: formatNumber,
    createOdometerCounter: createOdometerCounter,
    createSubscriberPoller: createSubscriberPoller
  };

  document.dispatchEvent(new CustomEvent('acerca-counter-animation:ready'));
})();
